import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { DocumentDirectoryType } from '@econtractor/types';
import * as apiDocument from 'api/apiDocument';
import DocumentEditorOffcanvas, {
    type DocumentEditorMode,
    type DocumentItemsAppend,
    type DocumentItemsOverride,
} from './DocumentEditorOffcanvas';
import TemplateDirSelectModal, { type TemplateDirOption } from '../share/TemplateDirSelectModal';
import { getObjectRecord, parseJsonObject } from '../share/OffKeys/keysUtils';
import {
    buildObjectDocumentAppendItems,
    type ObjectDocumentIncludeFlags,
} from './model/buildObjectDocumentAppendItems';

const CREATE_NEW_DIR_ID = 0;
const DOCUMENT_TYPES: DocumentDirectoryType[] = ['contract', 'invoice', 'act', 'other'];

const modeByDocumentType = (type: DocumentDirectoryType): Exclude<DocumentEditorMode, 'dir'> => {
    if (type === 'invoice') return 'invoice';
    if (type === 'act') return 'complect';
    return 'object';
};

const DEFAULT_INCLUDE_FLAGS: ObjectDocumentIncludeFlags = {
    includeMaterials: false,
    includeTeamExpends: false,
    includeOtherExpends: false,
};

const pickNonEmptyEmail = (...values: unknown[]): string => {
    for (const value of values) {
        const email = String(value ?? '').trim();
        if (email) return email;
    }
    return '';
};

export const resolveObjectClientEmail = (objectData: unknown): string => {
    const record = getObjectRecord(objectData);
    const customer = parseJsonObject(record?.objectCustomer);
    const root = objectData && typeof objectData === 'object' ? (objectData as Record<string, unknown>) : null;
    const lead = root?.lead && typeof root.lead === 'object' ? (root.lead as Record<string, unknown>) : null;
    return pickNonEmptyEmail(
        customer?.email,
        record?.clientEmail,
        record?.client_email,
        lead?.clientEmail,
        lead?.client_email,
    );
};

export const resolveObjectName = (objectData: unknown): string => {
    const record = getObjectRecord(objectData);
    return String(record?.objectName || record?.ID || '').trim();
};

export const resolveObjectId = (objectData: unknown): number => {
    const record = getObjectRecord(objectData);
    return Number(record?.ID || 0);
};

interface ObjectDocumentEditorProps {
    objectData?: unknown;
    selectOpen: boolean;
    onSelectClose: () => void;
    onGenerated?: () => Promise<void> | void;
    /** When set with selectOpen, products/services come from this snapshot (e.g. accepted KP). */
    itemsOverride?: DocumentItemsOverride | null;
    onItemsOverrideClear?: () => void;
}

const ObjectDocumentEditor = ({
    objectData,
    selectOpen,
    onSelectClose,
    onGenerated,
    itemsOverride = null,
    onItemsOverrideClear,
}: ObjectDocumentEditorProps) => {
    const { t } = useTranslation(['Document', 'common']);
    const [showSelectModal, setShowSelectModal] = useState(false);
    const [loadingOptions, setLoadingOptions] = useState(false);
    const [dirOptions, setDirOptions] = useState<TemplateDirOption[]>([]);
    const [selectedDirId, setSelectedDirId] = useState(CREATE_NEW_DIR_ID);
    const [selectedType, setSelectedType] = useState<DocumentDirectoryType | ''>('');
    const [editId, setEditId] = useState<number | null>(null);
    const [templateDirId, setTemplateDirId] = useState<number | null>(null);
    const [activeType, setActiveType] = useState<DocumentDirectoryType>('contract');
    const [editorMode, setEditorMode] = useState<Exclude<DocumentEditorMode, 'dir'>>('object');
    const [activeItemsOverride, setActiveItemsOverride] = useState<DocumentItemsOverride | null>(null);
    const [activeItemsAppend, setActiveItemsAppend] = useState<DocumentItemsAppend | null>(null);
    const [includeFlags, setIncludeFlags] = useState<ObjectDocumentIncludeFlags>(DEFAULT_INCLUDE_FLAGS);

    const objectId = useMemo(() => resolveObjectId(objectData), [objectData]);
    const objectName = useMemo(() => resolveObjectName(objectData), [objectData]);
    const clientEmail = useMemo(() => resolveObjectClientEmail(objectData), [objectData]);
    const isFromProposal = Boolean(itemsOverride || activeItemsOverride);

    const typeOptions = useMemo(
        () =>
            DOCUMENT_TYPES.map((type) => ({
                value: type,
                label: t(`documentTypes.${type}`),
            })),
        [t],
    );

    const includeSwitches = useMemo(() => {
        if (isFromProposal) return [];
        return [
            {
                id: 'materials',
                label: t('object.includeMaterialsLabel'),
                hint: t('object.includeMaterialsHint'),
                checked: includeFlags.includeMaterials,
                onChange: (checked: boolean) =>
                    setIncludeFlags((prev) => ({ ...prev, includeMaterials: checked })),
            },
            {
                id: 'team',
                label: t('object.includeTeamExpendsLabel'),
                hint: t('object.includeTeamExpendsHint'),
                checked: includeFlags.includeTeamExpends,
                onChange: (checked: boolean) =>
                    setIncludeFlags((prev) => ({ ...prev, includeTeamExpends: checked })),
            },
            {
                id: 'other',
                label: t('object.includeOtherExpendsLabel'),
                hint: t('object.includeOtherExpendsHint'),
                checked: includeFlags.includeOtherExpends,
                onChange: (checked: boolean) =>
                    setIncludeFlags((prev) => ({ ...prev, includeOtherExpends: checked })),
            },
        ];
    }, [includeFlags, isFromProposal, t]);

    const loadDirOptions = async (documentType: DocumentDirectoryType) => {
        setLoadingOptions(true);
        const result = await apiDocument.getDirList({
            order: 'ORDER BY proposal_name',
            page: 1,
            onPage: 999999,
            proposalType: documentType,
        });
        const options = (result?.documents || []).map((row) => ({
            value: Number(row.ID),
            label: row.proposalName || `#${row.ID}`,
        }));
        setDirOptions(options);
        setSelectedDirId(CREATE_NEW_DIR_ID);
        setLoadingOptions(false);
    };

    useEffect(() => {
        if (!selectOpen) return;
        setSelectedType('');
        setDirOptions([]);
        setSelectedDirId(CREATE_NEW_DIR_ID);
        setLoadingOptions(false);
        setIncludeFlags(DEFAULT_INCLUDE_FLAGS);
        setActiveItemsOverride(itemsOverride || null);
        setActiveItemsAppend(null);
        setShowSelectModal(true);
    }, [selectOpen, itemsOverride]);

    const handleTypeChange = (type: string) => {
        if (!DOCUMENT_TYPES.includes(type as DocumentDirectoryType)) {
            setSelectedType('');
            setDirOptions([]);
            setSelectedDirId(CREATE_NEW_DIR_ID);
            return;
        }
        const nextType = type as DocumentDirectoryType;
        setSelectedType(nextType);
        void loadDirOptions(nextType);
    };

    const clearSeed = () => {
        setActiveItemsOverride(null);
        setActiveItemsAppend(null);
        setIncludeFlags(DEFAULT_INCLUDE_FLAGS);
        onItemsOverrideClear?.();
    };

    const handleSelectClose = () => {
        setShowSelectModal(false);
        clearSeed();
        onSelectClose();
    };

    const handleConfirm = () => {
        if (!selectedType) return;
        const fromProposal = Boolean(activeItemsOverride);
        setActiveType(selectedType);
        setEditorMode(modeByDocumentType(selectedType));
        setTemplateDirId(selectedDirId === CREATE_NEW_DIR_ID ? null : selectedDirId);
        setActiveItemsAppend(
            fromProposal ? null : buildObjectDocumentAppendItems(objectData, includeFlags),
        );
        setShowSelectModal(false);
        onSelectClose();
        setEditId(0);
    };

    const handleEditorClose = () => {
        setEditId(null);
        setTemplateDirId(null);
        clearSeed();
    };

    return (
        <>
            <DocumentEditorOffcanvas
                mode={editorMode}
                objectData={objectData}
                objectId={objectId}
                objectName={objectName}
                clientEmail={clientEmail}
                defaultDocumentType={activeType}
                templateDirId={templateDirId}
                itemsOverride={activeItemsOverride}
                itemsAppend={activeItemsAppend}
                documentId={editId}
                onClose={handleEditorClose}
                onSaved={() => undefined}
                onGenerated={onGenerated}
            />

            <TemplateDirSelectModal
                show={showSelectModal}
                title={
                    activeItemsOverride
                        ? t('object.selectTitleFromProposal')
                        : t('object.selectTitle')
                }
                typeLabel={t('object.selectTypeLabel')}
                typePlaceholder={t('object.selectTypePlaceholder')}
                typeOptions={typeOptions}
                selectedType={selectedType}
                onSelectedTypeChange={handleTypeChange}
                label={t('object.selectLabel')}
                createNewLabel={t('object.selectCreateNew')}
                options={dirOptions}
                selectedId={selectedDirId}
                onSelectedIdChange={setSelectedDirId}
                loadingOptions={loadingOptions}
                disableTemplateSelect={!selectedType}
                disableConfirm={!selectedType || loadingOptions}
                includeSwitches={includeSwitches}
                onHide={handleSelectClose}
                onConfirm={handleConfirm}
            />
        </>
    );
};

export default ObjectDocumentEditor;
export type { DocumentItemsOverride };
